/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmationButtons, CustomDialog } from '@opengeoweb/shared';
import type {
  AviationProduct,
  Sigmet,
  SigmetConfig,
} from '@opengeoweb/sigmet-airmet';
import {
  CancelSigmetVolcanicMovements,
  prepareCancelSigmet,
} from '@opengeoweb/sigmet-airmet';
import { CancelAviationDialogContent } from './AviationWarningCancelDialogContent';
import { humanizeApiErrorMessage } from '../../api/aviation-api/errorHandling';

type CancelType = 'sigmet' | 'airmet';

export interface CancelAviationConfirmDialogProps {
  open: boolean;
  type: CancelType;
  product: AviationProduct;
  showVA: boolean;
  aviationConfig?: SigmetConfig;
  sigmet?: Sigmet;
  confirmLabel: string;
  title: string;
  onClose: () => void;
  cancelAviationProduct: (
    p: AviationProduct,
    t: CancelType,
    opts?: { vaSigmetMoveToFIR?: string },
  ) => Promise<unknown>;
  onSuccess?: () => void;
}

export const CancelAviationConfirmDialog: React.FC<
  CancelAviationConfirmDialogProps
> = ({
  open,
  type,
  product,
  showVA,
  aviationConfig,
  sigmet,
  confirmLabel,
  title,
  onClose,
  cancelAviationProduct,
  onSuccess,
}) => {
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const vaForm = useForm<{ vaSigmetMoveToFIR?: string }>({
    mode: 'onChange',
    defaultValues: {},
  });
  const { getValues, watch } = vaForm;
  const selectedMoveToFIR = watch('vaSigmetMoveToFIR');
  const cancelProductForPreview = React.useMemo(() => {
    if (!showVA) {
      return product;
    }
    return prepareCancelSigmet(
      product as Sigmet,
      selectedMoveToFIR ?? 'UNKNOWN',
    );
  }, [product, selectedMoveToFIR, showVA]);

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      await cancelAviationProduct(product, type, {
        vaSigmetMoveToFIR: showVA ? getValues('vaSigmetMoveToFIR') : undefined,
      });
      setIsSubmitting(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setIsSubmitting(false);
      setErrorMsg(
        `Failed to cancel ${type.toUpperCase()}: ${humanizeApiErrorMessage(err)}`,
      );
    }
  };

  return (
    <CustomDialog
      actions={
        <ConfirmationButtons
          cancelLabel="Close"
          confirmLabel={confirmLabel}
          isLoading={isSubmitting}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      }
      data-testid="cancel-aviation-dialog"
      description=""
      isResizable
      onClose={onClose}
      open={open}
      resizableDefaultSize={{ width: 560, height: 'auto' }}
      title={title}
    >
      <CancelAviationDialogContent
        cancelProduct={cancelProductForPreview}
        errorMessage={errorMsg ?? undefined}
        product={product}
        warningType={type}
      />
      {showVA ? (
        <FormProvider {...vaForm}>
          <Box component="hr" sx={{ my: 1 }} />
          <CancelSigmetVolcanicMovements
            productConfig={aviationConfig!}
            sigmet={sigmet!}
          />
        </FormProvider>
      ) : null}
    </CustomDialog>
  );
};
